import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export const useDevis = (options = {}) => {
  const { autoLoad = true, filters = {} } = options;
  const filtersRef = useRef(filters);

  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDevis = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filtersRef.current, ...customFilters };
      const response = await api.devis.getAll(params);
      setDevis(response.devis || []);
      return response.devis;
    } catch (err) {
      const message = err.message || 'Erreur lors du chargement des devis';
      setError(message);
      console.error('Erreur loadDevis:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createDevis = useCallback(async (data) => {
    setError(null);
    try {
      const response = await api.devis.create(data);
      const newDevis = response.devis;
      setDevis(prev => [newDevis, ...prev]);
      return { success: true, devis: newDevis };
    } catch (err) {
      const message = err.message || 'Erreur lors de la création';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const updateDevis = useCallback(async (id, data) => {
    setError(null);
    try {
      const response = await api.devis.update(id, data);
      const updated = response.devis;
      setDevis(prev => prev.map(d => d.id === id ? updated : d));
      return { success: true, devis: updated };
    } catch (err) {
      const message = err.message || 'Erreur lors de la mise à jour';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const deleteDevis = useCallback(async (id) => {
    setError(null);
    try {
      await api.devis.delete(id);
      setDevis(prev => prev.filter(d => d.id !== id));
      return { success: true };
    } catch (err) {
      const message = err.message || 'Erreur lors de la suppression';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const envoyer = useCallback(async (id) => {
    setError(null);
    try {
      const response = await api.devis.envoyer(id);
      const updated = response.devis;
      setDevis(prev => prev.map(d => d.id === id ? updated : d));
      return { success: true, devis: updated };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const accepter = useCallback(async (id) => {
    setError(null);
    try {
      const response = await api.devis.accepter(id);
      const updated = response.devis;
      setDevis(prev => prev.map(d => d.id === id ? updated : d));
      return { success: true, devis: updated };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const refuser = useCallback(async (id) => {
    setError(null);
    try {
      const response = await api.devis.refuser(id);
      const updated = response.devis;
      setDevis(prev => prev.map(d => d.id === id ? updated : d));
      return { success: true, devis: updated };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadDevis();
    }
  }, [autoLoad, loadDevis]);

  return {
    devis,
    loading,
    error,
    loadDevis,
    createDevis,
    updateDevis,
    deleteDevis,
    envoyer,
    accepter,
    refuser,
  };
};

export default useDevis;
